import { ResetPassword } from '@/apis/users/requests.ts'
import { Button } from '@/app/common/components/button/Button.tsx'
import { CircleLoader } from '@/common/components/CircleLoader.tsx'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import { Input } from '@/app/common/components/input/Input.tsx'
import { EyeIcon, EyeSlashIcon } from '@/common/icons/general.tsx'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import AceScholarshipsLogo from '@assets/AceScholarshipLogo1.jpg'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { AppRoutePaths } from '../../../../routing/route_paths.ts'
import { AuthLayout } from '../layout.tsx'

export default function ResetPasswordPage() {
  const [passwordChanged, setPasswordChanged] = useState(false)

  return (
    <AuthLayout>
      <AuthLayout.Content className="space-y-5">
        {!passwordChanged && (
          <PasswordResetForm setPasswordChanged={setPasswordChanged} />
        )}
        {passwordChanged && <PasswordChangedView />}
      </AuthLayout.Content>
    </AuthLayout>
  )
}

interface PasswordResetFormProps {
  setPasswordChanged: (value: boolean) => void
}

const ResetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters long' })
      .regex(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/, {
        message:
          'Password must contain at least one uppercase letter, one lowercase letter, and one special character.',
      }),
    confirm_password: z.string().min(8, {
      message: 'Confirm Password must be at least 8 characters long',
    }),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  })
type ResetPasswordFormProps = z.infer<typeof ResetPasswordSchema>

const PasswordResetForm = ({ setPasswordChanged }: PasswordResetFormProps) => {
  const [loading, setLoading] = useState(false)
  const { fireAlert } = useAppAlert()
  const params = new URLSearchParams(location.search)
  const navigate = useNavigate()
  const [resetParams, setResetParams] = useState<null | QueryInvitation>(null)
  const [showNewPassword, setShowNewPassword] = useState(false)

  useEffect(() => {
    decodeBase64Invitation(params.get('q') ?? '')
      .then((qInvitation) => {
        setResetParams(qInvitation)
      })
      .catch(() => {
        fireAlert({
          variant: 'error',
          message: 'Invalid request',
        }).then(() => navigate(AppRoutePaths.auth.login))
      })
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormProps>({
    resolver: zodResolver(ResetPasswordSchema),
  })

  const onSubmit: SubmitHandler<ResetPasswordFormProps> = async (formData) => {
    setLoading(true)
    const { error } = await ResetPassword({
      user_id: resetParams?.user_id ?? '0',
      token: resetParams?.token ?? '',
      payload: {
        password: formData.password,
      },
    })
    setLoading(false)
    if (error) {
      await fireAlert({
        variant: 'error',
        message: error.message,
      })
      navigate(AppRoutePaths.auth.login)
    } else {
      setPasswordChanged(true)
    }
  }

  return (
    <>
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900">Reset Password</h3>
        <span className="text-sm text-gray-500 font-medium">
          Enter your new password
        </span>
      </div>
      <form noValidate className="space-y-3" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            New Password
          </label>
          <div className="mt-1 relative flex">
            <Input
              {...register('password')}
              type={showNewPassword ? 'text' : 'password'}
              required
              placeholder="Enter a new password"
            />
            <div
              className="absolute right-0 flex items-center h-full mr-2"
              onClick={() => {
                setShowNewPassword(!showNewPassword)
              }}
            >
              {showNewPassword ? <EyeIcon /> : <EyeSlashIcon />}
            </div>
          </div>
          <FormErrorMessage message={errors.password?.message} />
        </div>

        <div className="pb-8">
          <label
            htmlFor="email"
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            Confirm New Password
          </label>
          <div className="mt-1">
            <Input
              {...register('confirm_password')}
              type="password"
              required
              placeholder="Re-enter a new password"
            />
            <FormErrorMessage message={errors.confirm_password?.message} />
          </div>
        </div>

        <div className="flex">
          <Button type="submit" className="grow space-x-2" disabled={loading}>
            {loading && <CircleLoader size="sm" />}
            <span>{loading ? 'Processing...' : 'Submit'}</span>
          </Button>
        </div>
      </form>
    </>
  )
}

const PasswordChangedView = () => {
  const navigate = useNavigate()
  const handleSubmit = () => {
    navigate(AppRoutePaths.auth.login)
  }
  return (
    <div className="flex flex-col">
      <img
        alt="image"
        className="light:hidden max-h-[180px] "
        src={AceScholarshipsLogo}
      />
      <h3 className="text-lg font-semibold text-gray-900 text-center mt-8 mb-4">
        Your password has changed
      </h3>
      <div className="text-sm text-gray-500 font-medium text-center mb-7">
        Your password has been successfully updated.
        <br />
        Your account's security is our priority.
      </div>
      <div className="flex justify-center">
        <Button onClick={handleSubmit}>Log In</Button>
      </div>
    </div>
  )
}

const querySchema = z.object({
  user_id: z.string(),
  email: z.string().email(),
  token: z.string(),
})
type QueryInvitation = z.infer<typeof querySchema>

async function decodeBase64Invitation(
  base64Str: string
): Promise<QueryInvitation> {
  const decodedUrlQueryString = atob(base64Str)

  // Create a URLSearchParams object to parse the query string
  const params = new URLSearchParams(decodedUrlQueryString)

  // Convert URLSearchParams to an object
  const queryObj: { [key: string]: string } = {}
  params.forEach((value, key) => {
    queryObj[key] = value
  })

  // Validate the query object against the schema
  return querySchema.parse(queryObj)
}
